
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Shield, CheckCircle, Users, Globe, Award, TrendingUp, BarChart3, Eye, Heart, Star, Calendar, Trophy, MessageCircle, Phone, Mail } from "lucide-react";
import StickyNavigation from "@/components/layout/StickyNavigation";
import { useTenant } from "@/contexts/TenantContext";
import { useEffect } from "react";
import { getPageTitle } from "@/utils/PageUtils";

const AboutUs = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();

  // Tenant-aware prefix for navigation from About Us page
  const subMatch = window.location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
  const subPrefix = subMatch ? `/site/${subMatch[1]}` : '';
  const tLink = (path: string) => `${subPrefix}${path}`;
  const pageTitle = "About Our Partnership";

  useEffect(() => {
    document.title = getPageTitle(pageTitle);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <StickyNavigation />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section - Partnership Focused */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {pageTitle}
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            {(tenant?.name || 'Our partner')} and YallaCompare have formed a partnership to bring simple, transparent business insurance to companies across the UAE. YallaCompare is an experienced UAE insurance broker committed to excellent service.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button size="lg" onClick={() => navigate(tLink('/decision-tree'))}>Start Decision Matrix</Button>
            <Button size="lg" variant="outline" onClick={() => navigate(tLink('/'))}>Browse Products</Button>
          </div>
        </div>

        {/* What the partnership means */}
        <Card className="mb-10">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Business insurance made easier</CardTitle>
            <CardDescription className="text-base">What this partnership delivers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Shield className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-1">UAE broker expertise</h3>
                <p className="text-gray-600 text-sm">Backed by YallaCompare, an established UAE insurance broker delivering compliant coverage and local support.</p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="h-7 w-7 text-green-600" />
                </div>
                <h3 className="font-semibold mb-1">Tailored business cover</h3>
                <p className="text-gray-600 text-sm">From employee medical and workers’ compensation to liability, property and cyber—matched to your operations.</p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Users className="h-7 w-7 text-purple-600" />
                </div>
                <h3 className="font-semibold mb-1">Service you can trust</h3>
                <p className="text-gray-600 text-sm">We prioritise fast responses, clear communication and support from first quote through to claims.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How it works */}
        <Card className="mb-10">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">How it works</CardTitle>
            <CardDescription>Three simple steps</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold mb-1">1) Tell us about your business</p>
                <p className="text-gray-600 text-sm">Use the Decision Matrix to assess mandatory and recommended cover for your UAE operations.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold mb-1">2) Compare and confirm</p>
                <p className="text-gray-600 text-sm">Our team helps you compare options and right‑size limits for your needs and budget.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold mb-1">3) Get protected</p>
                <p className="text-gray-600 text-sm">Activate your policy and get ongoing service from YallaCompare, your UAE broker partner.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Partnership reassurance */}
        <Card className="mb-16">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Why choose this partnership</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <h4 className="font-semibold mb-1">Local to the UAE</h4>
                <p className="text-gray-600 text-sm">Compliance with UAE regulations and guidance on mandatory covers.</p>
              </div>
              <div>
                <h4 className="font-semibold mb-1">Transparent pricing</h4>
                <p className="text-gray-600 text-sm">Clear options and documentation so you can buy with confidence.</p>
              </div>
              <div>
                <h4 className="font-semibold mb-1">End‑to‑end service</h4>
                <p className="text-gray-600 text-sm">From first quote to claims support through YallaCompare, your broker partner.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="mb-16 bg-blue-600 text-white">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl mb-4 text-white">Get in Touch</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <a href="tel:+971586948688" className="flex flex-col items-center hover:text-blue-200 transition-colors">
                <Phone className="h-8 w-8 mb-2" />
                <span className="font-semibold">Call Us</span>
                <span>+971586948688</span>
              </a>
              <a href="mailto:business@yallacompare.com" className="flex flex-col items-center hover:text-blue-200 transition-colors">
                <Mail className="h-8 w-8 mb-2" />
                <span className="font-semibold">Email Us</span>
                <span>business@yallacompare.com</span>
              </a>
              <a href="https://wa.me/971586949645" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center hover:text-green-200 transition-colors">
                <MessageCircle className="h-8 w-8 mb-2" />
                <span className="font-semibold">WhatsApp</span>
                <span>+971586949645</span>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <Card className="bg-gradient-to-br from-blue-600 to-blue-700 text-white text-center">
          <CardContent className="pt-8">
            <h3 className="text-3xl font-bold mb-4">Ready to get protected?</h3>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Start your business insurance assessment in minutes. We’ll guide you to the right UAE cover and support you throughout.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button size="lg" className="hover:opacity-90" onClick={() => navigate(tLink('/decision-tree'))}>
                Start Decision Matrix
              </Button>
              <Button size="lg" className="bg-white text-blue-600 border-white hover:bg-blue-50 hover:text-blue-700" onClick={() => navigate(tLink('/'))}>
                Browse Products
              </Button>
            </div>

            {/* Footer Information */}
            <div className="border-t border-blue-400 pt-8 mt-8">
              <div className="text-sm text-blue-200 space-y-2 max-w-4xl mx-auto">
                <p>
                  Information found on yallacompare is provided for illustrative purposes only, rates are subject to change based on your personal circumstances.
                </p>
                <p>
                  You should always speak to a trained professional before taking out any form of finance.
                </p>
                <p>
                  Implications of non-payment: If you fail to keep up with repayments on your finance, your bank may file a report with the UAE credit rating agency which may impact your ability to borrow from UAE banks in the future.
                </p>
                <p className="font-semibold">
                  YOUR HOME MAY BE REPOSSESSED IF YOU DO NOT KEEP UP REPAYMENTS ON A MORTGAGE, LOAN OR ANY OTHER DEBT SECURED ON IT.
                </p>
                <p>
                  Insurance is provided by Yallacompare Insurance Broker LLC (formerly Gulf Resources Insurance Management Services Limited), an Insurance Broker regulated by the UAE Central Bank (registration #20).
                </p>
                <p className="pt-4">
                  © 2025 yallacompare. All Rights Reserved.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AboutUs;
